import { Customer, Hub, isDemoHub, parseDemoScenarios, useUserRole } from "common";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, ScrollView, Text, XStack, YStack } from "tamagui";

import DemoPanelDialog from "./DemoPanelDialog";

// Typed anywhere on Base Stations outside a text field. Nothing on the page
// hints that the demo homes exist, so on screen they pass for real installs.
const TRIGGER = "demo";

const isTextField = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

/**
 * Hidden entry to the demo controls: typing TRIGGER opens a picker of the
 * demo homes (common DEMO_HUB_IDS), and picking one opens its scenario panel.
 */
const DemoLauncher = ({
  hubs,
  hubCustomers,
}: {
  hubs: Hub[];
  hubCustomers: Map<string, Customer[]>;
}) => {
  const { t } = useTranslation();
  const { permissions } = useUserRole();
  const [picking, setPicking] = useState(false);
  const [demoHub, setDemoHub] = useState<Hub | null>(null);

  // Scenarios are written through updateHub, so only staff who can edit a
  // base station get the shortcut at all. Off while either dialog is open.
  const listening = permissions.hub.update && !picking && !demoHub;
  useEffect(() => {
    if (!listening) return undefined;
    let typed = "";
    let lastKeyAt = 0;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1 || isTextField(e.target)) return;
      // The word has to be typed in one go, not assembled over minutes.
      if (e.timeStamp - lastKeyAt > 1500) typed = "";
      lastKeyAt = e.timeStamp;
      typed = (typed + e.key.toLowerCase()).slice(-TRIGGER.length);
      if (typed === TRIGGER) setPicking(true);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [listening]);

  const demoHubs = useMemo(
    () => hubs.filter((hub) => isDemoHub(hub.id)).sort((a, b) => a.name.localeCompare(b.name)),
    [hubs],
  );

  return (
    <>
      {picking && (
        <Dialog open onOpenChange={(open) => !open && setPicking(false)}>
          <Dialog.Portal>
            <Dialog.Overlay backgroundColor="$black10" />
            <Dialog.Content
              width={480}
              maxWidth="95vw"
              maxHeight="90dvh"
              gap="$4"
              padding="$5"
              backgroundColor="$gray2"
              borderWidth={0}
            >
              <Dialog.Title
                size="$15"
                fontSize="$9.5"
                fontWeight="bold"
                $sm={{ lineHeight: 36 }}
              >
                {t("baseStations.demo.pickerTitle")}
              </Dialog.Title>
              <ScrollView maxHeight={520} flexShrink={1}>
                <YStack gap="$1">
                  {demoHubs.length === 0 && (
                    <Text color="$gray10">{t("baseStations.demo.pickerEmpty")}</Text>
                  )}
                  {demoHubs.map((hub) => {
                    const customer = hubCustomers.get(hub.id)?.[0];
                    const place = [hub.city, hub.state].filter(Boolean).join(", ");
                    const running = Object.values(parseDemoScenarios(hub.config)).filter(
                      (state) => state && !state.clearedAt,
                    ).length;
                    return (
                      <Button
                        key={hub.id}
                        chromeless
                        height="auto"
                        paddingVertical="$2"
                        justifyContent="flex-start"
                        onPress={() => {
                          setPicking(false);
                          setDemoHub(hub);
                        }}
                      >
                        <YStack flex={1} alignItems="flex-start">
                          <Text>{hub.name}</Text>
                          <Text fontSize="$1" color="$gray10">
                            {[customer?.name, place].filter(Boolean).join(" · ")}
                          </Text>
                        </YStack>
                        {running > 0 && (
                          <Text fontSize="$2" color="$blue10">
                            {t("baseStations.demo.runningCount", { count: running })}
                          </Text>
                        )}
                      </Button>
                    );
                  })}
                </YStack>
              </ScrollView>
              <XStack justifyContent="flex-end">
                <Button onPress={() => setPicking(false)}>{t("common.close")}</Button>
              </XStack>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog>
      )}
      {demoHub && <DemoPanelDialog hub={demoHub} onClose={() => setDemoHub(null)} />}
    </>
  );
};

export default DemoLauncher;
